/**
 * Agent Confidence Settings Configuration Page
 *
 * Manages AI agent confidence thresholds for automated document analysis
 * Allows administrators to configure confidence levels for different categories
 *
 * Features:
 * - Product Category confidence threshold slider (0.50 - 0.99)
 * - Sales Channel confidence threshold slider (0.50 - 0.99)
 * - Real-time validation and change tracking
 * - Save/Cancel functionality with unsaved changes detection
 * - Responsive slider design with custom styling
 * - Dynamic fill visualization showing confidence percentage
 *
 * Confidence Threshold Behavior:
 * - Values below threshold: Routed to "Agent Routed Exceptions" for human review
 * - Values above threshold: Accepted and applied automatically
 * - Range: 0.50 (50%) to 0.99 (99%) confidence
 *
 * State Management:
 * - productThreshold / channelThreshold: Current slider values
 * - savedProductThreshold / savedChannelThreshold: Last saved values
 * - hasChanges: Tracks if changes pending save
 * - loading: API data fetch state
 *
 * API Integration:
 * - GET agent/threshold-settings/: Fetch current thresholds
 * - POST agent/threshold-settings/: Save updated thresholds
 *
 * Dependencies:
 * - config_mapping: API response codes and constants
 * - server_utils: API communication functions
 * - config_ip_details: API server URL
 * - lucide-react: ChevronRight icon
 *
 * @component
 * @returns {JSX.Element} Agent confidence settings configuration page
 *
 * @example
 * // Usage in router
 * import AgentConfidenceSettings from './pages/agentconfig'
 * <Route path="/agent-confidence-settings" element={<AgentConfidenceSettings />} />
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { API_SUCCESS_RESPONSE_CODE } from "../../utils/config_mapping";
import { apiCall } from "../../utils/server_utils";
import { API_SERVER_URL } from "../../utils/config_ip_details";
import CommonIcon from '../common/customicon';
//import { useToast } from "@/hooks/use-toast";
import HeaderLogo  from "../structure/headerlogo";

/**
 * Custom CSS Styles for Range Input Sliders
 *
 * Defines cross-browser compatible slider styling:
 * - Product Slider: Dark blue (#0a3d91) fill for Product Categories threshold
 * - Channel Slider: Dark blue (#0a3d91) fill for Sales Channels threshold
 *
 * Features:
 * - Custom thumb (handle) styling with shadow effects
 * - Linear gradient background showing filled portion
 * - Hover effects with scale transformation
 * - Active state styling for user feedback
 * - Cross-browser support (webkit, moz, standard)
 *
 * CSS Custom Properties:
 * - --value: Percentage fill position (set dynamically via JavaScript)
 */
const sliderStyles = `
  /* Product slider - Dark blue filled, grey unfilled */
  .slider-product {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 8px;
    border-radius: 4px;
    background: #d3d3d3;
    outline: none;
  }

  .slider-product::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #0a3d91;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    position: relative;
    z-index: 2;
  }

  .slider-product::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #0a3d91;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  }

  .slider-product::-webkit-slider-runnable-track {
    width: 100%;
    height: 8px;
    cursor: pointer;
    background: linear-gradient(to right, #0a3d91 0%, #0a3d91 var(--value), #d3d3d3 var(--value), #d3d3d3 100%);
    border-radius: 4px;
  }

  .slider-product::-moz-range-track {
    width: 100%;
    height: 8px;
    cursor: pointer;
    background: #d3d3d3;
    border-radius: 4px;
  }

  .slider-product::-moz-range-progress {
    background: #0a3d91;
    height: 8px;
    border-radius: 4px;
  }

  /* Channel slider - Dark blue filled, grey unfilled */
  .slider-channel {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 8px;
    border-radius: 4px;
    background: #d3d3d3;
    outline: none;
  }

  .slider-channel::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #0a3d91;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    position: relative;
    z-index: 2;
  }

  .slider-channel::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #0a3d91;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  }

  .slider-channel::-webkit-slider-runnable-track {
    width: 100%;
    height: 8px;
    cursor: pointer;
    background: linear-gradient(to right, #0a3d91 0%, #0a3d91 var(--value), #d3d3d3 var(--value), #d3d3d3 100%);
    border-radius: 4px;
  }

  .slider-channel::-moz-range-track {
    width: 100%;
    height: 8px;
    cursor: pointer;
    background: #d3d3d3;
    border-radius: 4px;
  }

  .slider-channel::-moz-range-progress {
    background: #0a3d91;
    height: 8px;
    border-radius: 4px;
  }

  /* Hover effects - Visual feedback on interaction */
  .slider-product:hover::-webkit-slider-thumb,
  .slider-channel:hover::-webkit-slider-thumb {
    background: #082d6b;
    transform: scale(1.15);
    box-shadow: 0 3px 8px rgba(0,0,0,0.4);
  }

  .slider-product:hover::-moz-range-thumb,
  .slider-channel:hover::-moz-range-thumb {
    background: #082d6b;
    transform: scale(1.15);
    box-shadow: 0 3px 8px rgba(0,0,0,0.4);
  }

  /* Active/Focus effects - Feedback during dragging */
  .slider-product:active::-webkit-slider-thumb,
  .slider-channel:active::-webkit-slider-thumb {
    background: #061f4a;
    box-shadow: 0 1px 3px rgba(0,0,0,0.5);
  }

  .slider-product:active::-moz-range-thumb,
  .slider-channel:active::-moz-range-thumb {
    background: #061f4a;
    box-shadow: 0 1px 3px rgba(0,0,0,0.5);
  }
`;

/**
 * AgentConfidenceSettings Component
 *
 * Renders the agent confidence threshold configuration interface
 * Allows administrators to set confidence levels for AI agent automation
 *
 * Features:
 * - Dual threshold sliders (Product & Channel)
 * - Number input fields for precise value entry
 * - Percentage display and visualization
 * - Real-time validation (0.50 - 0.99 range)
 * - Save/Cancel with change detection
 * - Loading state during API operations
 *
 * Hooks:
 * - useState: Local state for thresholds and UI state
 * - useEffect: Fetch settings on mount, update slider fill on change
 * - useNavigate: Router navigation (prepared for future use)
 *
 * @function
 * @returns {JSX.Element} Configuration page with sliders and controls
 */
const AgentConfidenceSettings = () => {
  const navigate = useNavigate();
 // const { toast } = useToast();

  // ===== STATE: Current threshold values (controlled by sliders/inputs) =====
  const [savedProductThreshold, setSavedProductThreshold] = useState(0.9); // Last saved product threshold
  const [savedChannelThreshold, setSavedChannelThreshold] = useState(0.9); // Last saved channel threshold

  const [productThreshold, setProductThreshold] = useState(0.9); // Current product threshold being edited
  const [channelThreshold, setChannelThreshold] = useState(0.9); // Current channel threshold being edited

  // ===== STATE: UI state =====
  const [hasChanges, setHasChanges] = useState(false); // True if current values differ from saved
  const [loading, setLoading] = useState(true); // True while fetching settings from API

  /**
   * Fetch Agent Confidence Settings from API
   * Called on component mount to load current thresholds
   *
   * Process:
   * 1. Set loading state to true
   * 2. Call API GET agent/threshold-settings/
   * 3. Extract productThreshold and channelThreshold from response
   * 4. Update both current and saved state
   * 5. Handle errors gracefully
   *
   * @async
   * @function fetchAgentSettings
   * @returns {Promise<void>}
   */
  useEffect(() => {
    fetchAgentSettings();
  }, []);

  const fetchAgentSettings = async () => {
    try {
      setLoading(true);
      const response = await apiCall("GET", API_SERVER_URL + "agent/threshold-settings/");

      console.log("Agent Settings Response:", response);

      if (response.code === API_SUCCESS_RESPONSE_CODE && response.data) {
        // Extract thresholds from API response
        const { productThreshold: prodThreshold, channelThreshold: chanThreshold } = response.data;

        // Set both current and saved thresholds (no pending changes after fetch)
        setProductThreshold(prodThreshold || 0.9);
        setChannelThreshold(chanThreshold || 0.9);
        setSavedProductThreshold(prodThreshold || 0.9);
        setSavedChannelThreshold(chanThreshold || 0.9);
      }
    } catch (error) {
      console.error("Error fetching agent settings:", error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Track Changes and Validate Thresholds
   * Updates hasChanges state when thresholds are modified
   *
   * Change Detection:
   * - Compares current values against saved values
   * - Validates both values are in valid range (0.5 - 0.99)
   * - Only enables Save if changes exist AND values are valid
   *
   * @effect
   * @dependency {number} productThreshold - Product threshold value
   * @dependency {number} channelThreshold - Channel threshold value
   * @dependency {number} savedProductThreshold - Last saved product threshold
   * @dependency {number} savedChannelThreshold - Last saved channel threshold
   */
  useEffect(() => {
    const productChanged = productThreshold !== savedProductThreshold;
    const channelChanged = channelThreshold !== savedChannelThreshold;
    const productValid = productThreshold >= 0.5 && productThreshold <= 0.99;
    const channelValid = channelThreshold >= 0.5 && channelThreshold <= 0.99;

    setHasChanges(
      (productChanged || channelChanged) && productValid && channelValid
    );
  }, [productThreshold, channelThreshold, savedProductThreshold, savedChannelThreshold]);

  /**
   * Update Slider Visual Fill on Value Change
   * Dynamically updates CSS gradient to show filled portion
   *
   * Calculation:
   * 1. Get slider DOM elements (.slider-product, .slider-channel)
   * 2. Calculate percentage: (value - min) / (max - min) * 100
   * 3. Update CSS custom property --value with percentage
   * 4. Set background gradient with filled color (#0a3d91)
   *
   * Result:
   * - Left side: Dark blue (#0a3d91) representing filled portion
   * - Right side: Grey (#d3d3d3) representing empty portion
   * - Thumb (handle) overlays the gradient
   *
   * @effect
   * @dependency {number} productThreshold - Product threshold (affects product slider)
   * @dependency {number} channelThreshold - Channel threshold (affects channel slider)
   */
  useEffect(() => {
    const productSlider = document.querySelector('.slider-product');
    const channelSlider = document.querySelector('.slider-channel');

    // Update product slider fill
    if (productSlider) {
      const productPercent = ((productThreshold - 0.5) / (0.99 - 0.5)) * 100;
      productSlider.style.setProperty('--value', `${productPercent}%`);
      productSlider.style.background = `linear-gradient(to right, #0a3d91 0%, #0a3d91 ${productPercent}%, #d3d3d3 ${productPercent}%, #d3d3d3 100%)`;
    }

    // Update channel slider fill
    if (channelSlider) {
      const channelPercent = ((channelThreshold - 0.5) / (0.99 - 0.5)) * 100;
      channelSlider.style.setProperty('--value', `${channelPercent}%`);
      channelSlider.style.background = `linear-gradient(to right, #0a3d91 0%, #0a3d91 ${channelPercent}%, #d3d3d3 ${channelPercent}%, #d3d3d3 100%)`;
    }
  }, [productThreshold, channelThreshold]);

  /**
   * Handle Product Threshold Number Input Change
   * Parses input value and updates product threshold with bounds checking
   *
   * Validation:
   * - Parses string input to float
   * - Constrains value to range [0.5, 0.99]
   * - Skips update if input is not a valid number
   *
   * @function
   * @param {Event} e - Change event from number input
   */
  const handleProductInputChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setProductThreshold(Math.max(0.5, Math.min(0.99, value)));
    }
  };

  /**
   * Handle Channel Threshold Number Input Change
   * Parses input value and updates channel threshold with bounds checking
   *
   * Validation:
   * - Parses string input to float
   * - Constrains value to range [0.5, 0.99]
   * - Skips update if input is not a valid number
   *
   * @function
   * @param {Event} e - Change event from number input
   */
  const handleChannelInputChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setChannelThreshold(Math.max(0.5, Math.min(0.99, value)));
    }
  };

  /**
   * Cancel Changes - Reset to Last Saved Values
   * Discards any unsaved threshold modifications
   *
   * Process:
   * 1. Set current thresholds back to saved values
   * 2. Removes the unsaved changes indicator
   * 3. Slider and input values return to previous state
   *
   * @function
   */
  const handleCancel = () => {
    setProductThreshold(savedProductThreshold);
    setChannelThreshold(savedChannelThreshold);
  };

  /**
   * Save Changes to API
   * Persists updated threshold values to backend
   *
   * Process:
   * 1. Build payload with current threshold values
   * 2. Call API POST agent/threshold-settings/
   * 3. On success: Update saved values, clear unsaved indicator
   * 4. On failure: Show error alert
   * 5. Handle exceptions with try-catch
   *
   * Success Behavior:
   * - setSavedProductThreshold and setSavedChannelThreshold
   * - hasChanges becomes false
   * - Thresholds now active in AI system
   *
   * @async
   * @function handleSave
   * @returns {Promise<void>}
   */
  const handleSave = async () => {
    try {
      // Build save payload
      const payload = {
        productThreshold: productThreshold,
        channelThreshold: channelThreshold
      };

      console.log("Saving Agent Settings:", payload);

      // Call API to save settings
      const response = await apiCall("POST", API_SERVER_URL + "agent/threshold-settings/", payload);

      console.log("Save Response:", response);

      if (response.code === API_SUCCESS_RESPONSE_CODE) {
        // Update saved values to reflect new persistent state
        setSavedProductThreshold(productThreshold);
        setSavedChannelThreshold(channelThreshold);

        //alert("Agent confidence thresholds updated successfully!");
        // toast({
        //   title: "Agent confidence thresholds updated.",
        // });
      } else {
        alert("Failed to update settings. Please try again.");
      }
    } catch (error) {
      console.error("Error saving agent settings:", error);
      alert("Error saving settings: " + error.message);
    }
  };

  return (
    <div className="main-content" style={{ paddingTop: 64 }}>
       <HeaderLogo />
      {/* Inject custom slider styles */}
      <style>{sliderStyles}</style>

      <div className="page-content">
        <div className="container-fluid">
          <div id="table_height">
            {/* Page heading section */}
            <div className="flex-grow-1" id="row_heading">
              <div className="d-flex align-items-center mb-2">
              {/* Icon badge */}
              <div className="bg-primary text-white rounded p-2 me-3">
                <CommonIcon iconName="FileText" size={"24px"}  color="#fff" />
              </div>

              {/* Page title and description */}
              <div className="flex-grow-1">
                <h1 className="h3 mb-0 fw-bold">Agent Confidence Settings</h1>
                <p className="text-muted mb-0 small">Configure confidence thresholds for automated agent routing</p>
              </div>

              {/* Logo badge */}
     <div className="ms-auto bg-primary" style={{padding:"1px",border:"1px solid #000",position:"absolute", right:"5px",borderRadius:"2px",top:"15px"}}>
    <img src="../images/predatica_logo.png" alt="Logo" height={"20px"}  />
    </div>
            </div>

              </div>

            {/* Error message container */}
            <div id="app_err"></div>
          </div>

          {/* Main settings card */}
    <div className="py-4">

      {/* Card container */}
      <div className="card p-4 shadow-sm">
        {loading ? (
          // Loading state: Show spinner
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted mt-3">Loading settings...</p>
          </div>
        ) : (
          // Loaded state: Show settings controls
          <>
        <div className="mb-4">

          {/* PRODUCT THRESHOLD SECTION */}
          <label className="form-label fw-semibold">
            Product Categories Threshold
          </label>

          {/* Product threshold input + slider row */}
          <div className="d-flex align-items-center gap-3 mb-3">
            {/* Number input for precise value entry */}
            <input
              type="number"
              className="form-control"
              style={{ width: "120px" }}
              min="0.50"
              max="0.99"
              step="0.01"
              value={productThreshold.toFixed(2)}
              onChange={handleProductInputChange}
            />

            {/* Percentage display */}
            <span className="text-muted small" style={{ minWidth: "50px" }}>
              ({(productThreshold * 100).toFixed(0)}%)
            </span>

            {/* Slider control */}
            <div style={{ flex: 1, maxWidth: "400px" }}>
              <input
                type="range"
                className="form-range slider-product"
                min="0.5"
                max="0.99"
                step="0.01"
                value={productThreshold}
                onChange={(e) => setProductThreshold(parseFloat(e.target.value))}
              />
              {/* Min/Max labels */}
              <div className="d-flex justify-content-between text-muted small">
                <span>0.50</span>
                <span>0.99</span>
              </div>
            </div>
          </div>

          {/* Product threshold description */}
          <p className="text-muted small mt-2">
            Any Product Category mapping below this value goes to Agent Routed Exceptions.
          </p>

        </div>

        <hr />

        {/* CHANNEL THRESHOLD SECTION */}
        <div className="mt-4">
          <label className="form-label fw-semibold">
            Sales Channels Threshold
          </label>

          {/* Channel threshold input + slider row */}
          <div className="d-flex align-items-center gap-3 mb-3">
            {/* Number input for precise value entry */}
            <input
              type="number"
              className="form-control"
              style={{ width: "120px" }}
              min="0.50"
              max="0.99"
              step="0.01"
              value={channelThreshold.toFixed(2)}
              onChange={handleChannelInputChange}
            />

            {/* Percentage display */}
            <span className="text-muted small" style={{ minWidth: "50px" }}>
              ({(channelThreshold * 100).toFixed(0)}%)
            </span>

            {/* Slider control */}
            <div style={{ flex: 1, maxWidth: "400px" }}>
              <input
                type="range"
                className="form-range slider-channel"
                min="0.5"
                max="0.99"
                step="0.01"
                value={channelThreshold}
                onChange={(e) => setChannelThreshold(parseFloat(e.target.value))}
              />
              {/* Min/Max labels */}
              <div className="d-flex justify-content-between text-muted small">
                <span>0.50</span>
                <span>0.99</span>
              </div>
            </div>
          </div>

          {/* Channel threshold description */}
          <p className="text-muted small mt-2">
            Any Sales Channel mapping below this value goes to Agent Routed Exceptions.
          </p>
        </div>

        {/* Control Buttons */}
        <div className="d-flex justify-content-end gap-2 mt-4">
          {/* Cancel button - resets to last saved values */}
          <button className="btn btn-outline-secondary" onClick={handleCancel}>
            Cancel
          </button>
          {/* Save button - disabled if no changes or invalid values */}
          <button
            className="btn btn-primary"
            disabled={!hasChanges}
            onClick={handleSave}
          >
            Save changes
          </button>
        </div>
        </>
        )}
      </div>
    </div></div></div></div>
  );
};

export default AgentConfidenceSettings;
